import { useRef, useState } from "react";
import { Camera, Car, ChevronDown, ChevronUp, Loader2, MapPin, Navigation, Plus, Trash2, Upload, UserPlus } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { hoyISO } from "@/lib/format";
import ClienteBusquedaSelect from "@/components/ClienteBusquedaSelect";
import DireccionEspanaFields from "@/components/DireccionEspanaFields";
import MapaSelectorPunto from "@/components/MapaSelectorPunto";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

function importeParaInput(val) {
  if (val == null || val === "") return "";
  return String(val).replace(".", ",");
}

const CLIENTE_POTENCIAL_VACIO = {
  nombre: "", codigo: "", nif: "", email: "", telefono: "",
  direccion: "", poblacion: "", provincia: "", codigo_postal: "", notas: "",
  geocodificar: true,
};

function FotoOdometro({ titulo, preview, onFile, ocrKm, inputRef }) {
  return (
    <div className="flex flex-col items-center rounded-xl border border-dashed border-sky-200 bg-white p-3">
      <p className="mb-2 text-xs font-semibold text-sky-800">{titulo}</p>
      {preview ? (
        <img src={preview} alt={titulo} className="mb-2 max-h-36 w-full rounded-lg object-contain" />
      ) : (
        <Car className="mb-2 h-10 w-10 text-sky-200" />
      )}
      {ocrKm != null && (
        <p className="mb-1 text-[11px] text-emerald-700">OCR: {ocrKm} km (revisa y confirma)</p>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        capture="environment"
        className="hidden"
        onChange={(e) => onFile(e.target.files?.[0])}
      />
      <div className="flex gap-1.5">
        <Button type="button" variant="outline" size="sm" className="h-8 gap-1" onClick={() => inputRef.current?.click()}>
          <Upload className="h-3.5 w-3.5" /> Galería
        </Button>
        <Button type="button" variant="outline" size="sm" className="h-8 gap-1" onClick={() => inputRef.current?.click()}>
          <Camera className="h-3.5 w-3.5" /> Foto
        </Button>
      </div>
    </div>
  );
}

export default function KilometrajeForm({
  form,
  setForm,
  archivoInicio,
  setArchivoInicio,
  previewInicio,
  setPreviewInicio,
  archivoFin,
  setArchivoFin,
  previewFin,
  setPreviewFin,
  proyectos = [],
  clientes = [],
  rutas = [],
  usaProyectos = false,
  usaRutas = false,
  tarifaKmDefault = 0.26,
  onClienteCreado,
}) {
  const refIni = useRef(null);
  const refFin = useRef(null);
  const [ocrIni, setOcrIni] = useState(null);
  const [ocrFin, setOcrFin] = useState(null);
  const [escaneando, setEscaneando] = useState(false);
  const [calculando, setCalculando] = useState(false);
  const [mapaOpen, setMapaOpen] = useState(false);
  const [mapa, setMapa] = useState({ nombre: "", direccion: "", lat: null, lng: null });
  const [obteniendoUbicacion, setObteniendoUbicacion] = useState(false);
  const [clientePotOpen, setClientePotOpen] = useState(false);
  const [clientePot, setClientePot] = useState(null);
  const [guardandoCliente, setGuardandoCliente] = useState(false);

  const visitas = form.visitas || [];

  const escanear = async (file, cual) => {
    if (!file || !file.type.startsWith("image/")) {
      toast.error("Usa una foto JPG o PNG del cuentakilómetros");
      return;
    }
    if (cual === "inicio") {
      setArchivoInicio(file);
      setPreviewInicio(URL.createObjectURL(file));
    } else {
      setArchivoFin(file);
      setPreviewFin(URL.createObjectURL(file));
    }
    setEscaneando(true);
    const fd = new FormData();
    fd.append("imagen", file);
    try {
      const { data } = await api.post("/gastos/escanear-odometro", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (cual === "inicio") setOcrIni(data.km ?? null);
      else setOcrFin(data.km ?? null);
      if (data.km != null) {
        toast.message(`Lectura sugerida (${cual}): ${data.km} km`);
      }
      setForm((prev) => {
        const ini = cual === "inicio" ? data.km : ocrIni;
        const fin = cual === "fin" ? data.km : ocrFin;
        if (!prev.km && ini != null && fin != null && Number(fin) >= Number(ini)) {
          return { ...prev, km: importeParaInput(Math.round((Number(fin) - Number(ini)) * 10) / 10) };
        }
        return prev;
      });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setEscaneando(false);
    }
  };

  const addVisita = (v) => {
    setForm((prev) => ({
      ...prev,
      visitas: [...(prev.visitas || []), v],
      recorrido: null,
      km_ruta_estimada: "",
    }));
  };

  const quitarVisita = (idx) => {
    setForm((prev) => ({
      ...prev,
      visitas: (prev.visitas || []).filter((_, i) => i !== idx),
      recorrido: null,
      km_ruta_estimada: "",
    }));
  };

  const moverVisita = (idx, delta) => {
    setForm((prev) => {
      const list = [...(prev.visitas || [])];
      const j = idx + delta;
      if (j < 0 || j >= list.length) return prev;
      [list[idx], list[j]] = [list[j], list[idx]];
      return { ...prev, visitas: list, recorrido: null, km_ruta_estimada: "" };
    });
  };

  const calcular = async () => {
    const mantenerOrden = form.mantener_orden_visitas !== false;
    setCalculando(true);
    try {
      const { data } = await api.post("/gastos/calcular-recorrido-jornada", {
        fecha: form.fecha_gasto || hoyISO(),
        visitas,
        ruta_comercial_id: form.ruta_comercial_id ? Number(form.ruta_comercial_id) : undefined,
        mantener_orden: mantenerOrden,
      });
      setForm((prev) => ({
        ...prev,
        km_ruta_estimada: data.km_total != null ? String(data.km_total) : "",
        recorrido: data,
        visitas: data.paradas?.length ? data.paradas : prev.visitas,
        origen_direccion: data.origen?.direccion || data.origen?.nombre || prev.origen_direccion,
        destino_direccion: data.destino?.direccion || data.destino?.nombre || prev.destino_direccion,
        tarifa_km: prev.tarifa_km || importeParaInput(data.tarifa_km || tarifaKmDefault),
        importe: prev.km
          ? importeParaInput(
              Math.round(Number(String(prev.km).replace(",", ".")) * Number(data.tarifa_km || tarifaKmDefault) * 100) / 100
            )
          : importeParaInput(data.importe),
      }));
      if (data.km_total != null) {
        const orig = data.origen?.fuente_label || "Origen";
        const dest = data.destino?.fuente_label || "Destino";
        toast.success(`Ruta aproximada: ${data.km_total} km · ${orig} → ${dest}`);
      } else {
        toast.message("No hay GPS suficiente para estimar la ruta. Puedes registrar los km igual.");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCalculando(false);
    }
  };

  const guardarMapa = () => {
    if (mapa.lat == null || mapa.lng == null) {
      toast.error("Pulsa el mapa o busca una dirección");
      return;
    }
    addVisita({
      tipo: "mapa",
      nombre: mapa.nombre.trim() || mapa.direccion.trim() || "Ubicación",
      direccion: mapa.direccion.trim(),
      lat: mapa.lat,
      lng: mapa.lng,
    });
    setMapaOpen(false);
    setMapa({ nombre: "", direccion: "", lat: null, lng: null });
  };

  const geocodificarDir = async () => {
    if (!mapa.direccion.trim()) return;
    try {
      const fd = new FormData();
      fd.append("direccion", mapa.direccion.trim());
      const { data } = await api.post("/gastos/geocodificar", fd);
      setMapa((prev) => ({
        ...prev,
        lat: data.lat,
        lng: data.lng,
        direccion: data.direccion || prev.direccion,
        nombre: prev.nombre || data.direccion || "",
      }));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const obtenerUbicacionActual = () => {
    if (!navigator.geolocation) {
      toast.error("Tu navegador no permite obtener la ubicación");
      return;
    }
    setObteniendoUbicacion(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        setMapa((prev) => ({ ...prev, lat, lng }));
        try {
          const fd = new FormData();
          fd.append("lat", String(lat));
          fd.append("lng", String(lng));
          const { data } = await api.post("/gastos/geocodificar-inverso", fd);
          setMapa((prev) => ({
            ...prev,
            lat: data.lat ?? lat,
            lng: data.lng ?? lng,
            direccion: data.direccion || prev.direccion,
          }));
          toast.message("Ubicación actual obtenida");
        } catch {
          toast.message("Ubicación obtenida (sin dirección automática)");
        } finally {
          setObteniendoUbicacion(false);
        }
      },
      (err) => {
        setObteniendoUbicacion(false);
        if (err.code === err.PERMISSION_DENIED) {
          toast.error("Permiso de ubicación denegado. Actívalo en el navegador o dispositivo.");
        } else if (err.code === err.TIMEOUT) {
          toast.error("No se pudo obtener la ubicación a tiempo. Inténtalo de nuevo.");
        } else {
          toast.error("No se pudo obtener la ubicación actual");
        }
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 }
    );
  };

  const guardarClientePotencial = async () => {
    if (!clientePot?.nombre?.trim()) {
      toast.error("Indica el nombre del cliente");
      return;
    }
    setGuardandoCliente(true);
    try {
      const { data } = await api.post("/gastos/clientes-potenciales", clientePot);
      onClienteCreado?.(data);
      addVisita({
        tipo: "cliente",
        cliente_id: data.id,
        nombre: data.nombre,
        direccion: [data.direccion, data.poblacion].filter(Boolean).join(", "),
        lat: data.latitud,
        lng: data.longitud,
        es_potencial: true,
      });
      toast.success("Cliente potencial registrado. Se ha avisado a administración.");
      setClientePotOpen(false);
      setClientePot(null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGuardandoCliente(false);
    }
  };

  const yaEnRuta = new Set(visitas.filter((v) => v.cliente_id).map((v) => String(v.cliente_id)));
  const decl = form.km ? Number(String(form.km).replace(",", ".")) : null;
  const est = form.km_ruta_estimada ? Number(String(form.km_ruta_estimada).replace(",", ".")) : null;
  const varKm = decl != null && est != null ? Math.round((decl - est) * 10) / 10 : null;
  const recorrido = form.recorrido || null;

  return (
    <div className="space-y-3">
      <p className="text-xs text-sky-800/80">
        Foto del cuentakilómetros al inicio y al final (opcional). Los km los indicas tú; intentamos leerlos de la foto.
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        <FotoOdometro
          titulo="Cuentakilómetros inicial"
          preview={previewInicio}
          onFile={(f) => escanear(f, "inicio")}
          ocrKm={ocrIni}
          inputRef={refIni}
        />
        <FotoOdometro
          titulo="Cuentakilómetros final"
          preview={previewFin}
          onFile={(f) => escanear(f, "fin")}
          ocrKm={ocrFin}
          inputRef={refFin}
        />
      </div>
      {escaneando && (
        <p className="flex items-center gap-2 text-xs text-sky-700">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Leyendo cuentakilómetros…
        </p>
      )}

      {(usaProyectos || usaRutas) && (
        <div className="grid gap-3 sm:grid-cols-2">
          {usaProyectos && (
            <div>
              <Label>Proyecto</Label>
              <Select
                value={form.proyecto_id || "none"}
                onValueChange={(v) => setForm({ ...form, proyecto_id: v === "none" ? "" : v })}
              >
                <SelectTrigger><SelectValue placeholder="Sin proyecto" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin proyecto</SelectItem>
                  {proyectos.map((a) => (
                    <SelectItem key={a.id} value={String(a.proyecto_id)}>
                      #{a.proyecto_codigo || a.proyecto_id} {a.proyecto_nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {usaRutas && (
            <div>
              <Label>Ruta comercial</Label>
              <Select
                value={form.ruta_comercial_id || "none"}
                onValueChange={(v) => setForm({ ...form, ruta_comercial_id: v === "none" ? "" : v })}
              >
                <SelectTrigger><SelectValue placeholder="Sin ruta" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin ruta</SelectItem>
                  {rutas.map((r) => (
                    <SelectItem key={r.id} value={String(r.id)}>{r.titulo || `Ruta ${r.fecha}`}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      )}

      <div className="rounded-lg border border-violet-100 bg-violet-50/40 p-3 space-y-2">
        <p className="text-xs font-semibold text-violet-900">Memoria de visitas</p>
        <p className="text-[11px] text-violet-800/80">
          Añade clientes visitados o una ubicación en el mapa. Puedes reordenar las visitas con las flechas. Al calcular la ruta usamos el picado de entrada y salida del día; si no hay GPS, el centro de trabajo.
        </p>
        {usaProyectos || usaRutas ? (
          <div className="flex flex-col gap-2 sm:flex-row">
            <div className="min-w-0 flex-1">
              <ClienteBusquedaSelect
                clientes={clientes}
                yaEnRuta={yaEnRuta}
                placeholder="Añadir cliente visitado…"
                onSelect={(c) => addVisita({
                  tipo: "cliente",
                  cliente_id: c.id,
                  nombre: c.nombre,
                  direccion: [c.direccion, c.poblacion].filter(Boolean).join(", "),
                  lat: c.latitud,
                  lng: c.longitud,
                  es_potencial: Boolean(c.es_potencial),
                })}
              />
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="shrink-0 gap-1 border-violet-200 text-violet-900"
              onClick={() => { setClientePot({ ...CLIENTE_POTENCIAL_VACIO }); setClientePotOpen(true); }}
            >
              <UserPlus className="h-3.5 w-3.5" /> Nuevo potencial
            </Button>
          </div>
        ) : null}
        <Button type="button" variant="outline" size="sm" className="gap-1" onClick={() => setMapaOpen(true)}>
          <MapPin className="h-3.5 w-3.5" /> Añadir ubicación (mapa)
        </Button>
        {visitas.length > 0 && (
          <ul className="space-y-1">
            {visitas.map((v, i) => (
              <li key={`${v.cliente_id || v.nombre}-${i}`} className="rounded-md bg-white px-2 py-1.5 text-sm">
                <div className="flex items-center justify-between gap-2">
                  <span className="min-w-0 truncate">
                    {String(i + 1).padStart(2, "0")}. {v.nombre || v.direccion}
                    <span className="ml-1 text-[10px] text-zinc-400">
                      {v.tipo === "cliente" ? (v.es_potencial ? "cliente potencial" : "cliente") : "mapa"}
                    </span>
                  </span>
                  <div className="flex shrink-0 items-center gap-0.5">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 px-0"
                      disabled={i === 0}
                      onClick={() => moverVisita(i, -1)}
                      title="Subir visita"
                    >
                      <ChevronUp className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 px-0"
                      disabled={i === visitas.length - 1}
                      onClick={() => moverVisita(i, 1)}
                      title="Bajar visita"
                    >
                      <ChevronDown className="h-3.5 w-3.5" />
                    </Button>
                    <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-rose-600" onClick={() => quitarVisita(i)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
                {recorrido && v.km_desde_anterior != null && (
                  <p className="mt-0.5 text-[10px] text-sky-700">
                    +{Number(v.km_desde_anterior).toFixed(1)} km desde el punto anterior
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div>
          <Label>Km recorridos</Label>
          <Input value={form.km} onChange={(e) => setForm({ ...form, km: e.target.value })} placeholder="0,0" />
        </div>
        <div>
          <Label>€ / km</Label>
          <Input value={form.tarifa_km} onChange={(e) => setForm({ ...form, tarifa_km: e.target.value })} />
        </div>
        <div>
          <Label>Km ruta aprox.</Label>
          <Input value={form.km_ruta_estimada || ""} readOnly className="bg-zinc-50" placeholder="—" />
        </div>
        <div>
          <Label>Variación</Label>
          <Input value={varKm == null ? "" : `${varKm > 0 ? "+" : ""}${varKm}`} readOnly className="bg-zinc-50" placeholder="—" />
        </div>
      </div>
      <div className="flex items-start gap-2 rounded-lg border border-sky-100 bg-sky-50/40 px-3 py-2">
        <Checkbox
          id="mantener-orden-visitas"
          checked={form.mantener_orden_visitas !== false}
          onCheckedChange={(v) => setForm({ ...form, mantener_orden_visitas: v === true })}
        />
        <Label htmlFor="mantener-orden-visitas" className="cursor-pointer text-xs leading-snug text-sky-950">
          Mantener el orden de las visitas (solo calcular kilómetros, sin reordenar la ruta)
        </Label>
      </div>
      <Button type="button" variant="outline" className="w-full gap-2" disabled={calculando} onClick={calcular}>
        {calculando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Car className="h-4 w-4" />}
        Calcular ruta aproximada del día
      </Button>

      {recorrido && (
        <div className="rounded-lg border border-sky-100 bg-sky-50/60 p-3 space-y-2 text-xs text-sky-950">
          <p className="font-semibold text-sky-900">Origen y destino del cálculo</p>
          {recorrido.origen && (
            <div className="rounded-md bg-white/80 px-2 py-1.5">
              <p className="font-medium text-emerald-800">Origen · {recorrido.origen.fuente_label || "Inicio"}</p>
              <p className="text-zinc-700">{recorrido.origen.nombre || "Inicio de jornada"}</p>
              {recorrido.origen.direccion && (
                <p className="text-[11px] text-zinc-500 line-clamp-2">{recorrido.origen.direccion}</p>
              )}
              {recorrido.origen.hora && (
                <p className="text-[10px] text-zinc-400">Picado: {recorrido.origen.hora}</p>
              )}
            </div>
          )}
          {recorrido.destino && (
            <div className="rounded-md bg-white/80 px-2 py-1.5">
              <p className="font-medium text-rose-800">Destino · {recorrido.destino.fuente_label || "Fin"}</p>
              <p className="text-zinc-700">{recorrido.destino.nombre || "Fin de jornada"}</p>
              {recorrido.destino.direccion && recorrido.destino.direccion !== recorrido.destino.nombre && (
                <p className="text-[11px] text-zinc-500 line-clamp-2">{recorrido.destino.direccion}</p>
              )}
              {recorrido.destino.hora && (
                <p className="text-[10px] text-zinc-400">Picado: {recorrido.destino.hora}</p>
              )}
              {recorrido.destino.fuente === "centro_sin_salida" && (
                <p className="text-[10px] text-sky-700">Se incluye el retorno al centro al no haber picado de cierre.</p>
              )}
            </div>
          )}
          {(recorrido.tramos || []).length > 0 && (
            <div className="space-y-1">
              <p className="font-medium text-sky-900">Km entre puntos</p>
              <ul className="space-y-0.5">
                {recorrido.tramos.map((t, idx) => (
                  <li key={`${t.desde}-${t.hasta}-${idx}`} className="flex justify-between gap-2 rounded bg-white/70 px-2 py-1 text-[11px]">
                    <span className="min-w-0 truncate">{t.desde_nombre || "?"} → {t.hasta_nombre || "?"}</span>
                    <span className="shrink-0 font-medium">{t.km != null ? `${Number(t.km).toFixed(1)} km` : "—"}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <div>
        <Label>Fecha</Label>
        <Input type="date" value={form.fecha_gasto} onChange={(e) => setForm({ ...form, fecha_gasto: e.target.value })} />
      </div>
      <div>
        <Label>Notas / memoria</Label>
        <Textarea
          rows={2}
          value={form.notas}
          onChange={(e) => setForm({ ...form, notas: e.target.value })}
          placeholder="Incidencias, peajes, observaciones del recorrido…"
        />
      </div>

      <Dialog open={mapaOpen} onOpenChange={setMapaOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Ubicación visitada</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <div>
              <Label>Nombre</Label>
              <Input value={mapa.nombre} onChange={(e) => setMapa({ ...mapa, nombre: e.target.value })} placeholder="Cliente, nave, punto de encuentro…" />
            </div>
            <div>
              <Label>Dirección</Label>
              <div className="flex gap-2">
                <Input value={mapa.direccion} onChange={(e) => setMapa({ ...mapa, direccion: e.target.value })} placeholder="Calle, población…" />
                <Button type="button" variant="outline" onClick={geocodificarDir}>Buscar</Button>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-2 w-full gap-1.5"
                disabled={obteniendoUbicacion}
                onClick={obtenerUbicacionActual}
              >
                {obteniendoUbicacion ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Navigation className="h-3.5 w-3.5" />
                )}
                Usar mi ubicación actual
              </Button>
            </div>
            <MapaSelectorPunto
              lat={mapa.lat}
              lng={mapa.lng}
              height={220}
              onChange={(lat, lng) => setMapa((prev) => ({ ...prev, lat, lng }))}
            />
            {mapa.lat != null && (
              <a
                className="text-xs text-sky-600 hover:underline"
                href={`https://www.google.com/maps?q=${mapa.lat},${mapa.lng}`}
                target="_blank"
                rel="noreferrer"
              >
                Abrir en Google Maps
              </a>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMapaOpen(false)}>Cancelar</Button>
            <Button onClick={guardarMapa} className="gap-1"><Plus className="h-4 w-4" /> Añadir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={clientePotOpen} onOpenChange={(v) => { setClientePotOpen(v); if (!v) setClientePot(null); }}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nuevo cliente potencial</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-violet-800/80">
            Se registrará como cliente potencial y se avisará al encargado y administración para completar la ficha.
          </p>
          {clientePot && (
            <div className="space-y-3">
              {[["nombre", "Nombre *"], ["codigo", "Código"], ["nif", "NIF"], ["email", "Email"], ["telefono", "Teléfono"], ["direccion", "Dirección"]].map(([key, label]) => (
                <div key={key}>
                  <Label>{label}</Label>
                  <Input value={clientePot[key] || ""} onChange={(e) => setClientePot({ ...clientePot, [key]: e.target.value })} />
                </div>
              ))}
              <DireccionEspanaFields
                value={clientePot}
                onChange={(next) => setClientePot({
                  ...clientePot,
                  codigo_postal: next.codigo_postal || "",
                  poblacion: next.poblacion || "",
                  provincia: next.provincia || "",
                })}
              />
              <div>
                <Label>Notas</Label>
                <Textarea
                  rows={2}
                  value={clientePot.notas || ""}
                  onChange={(e) => setClientePot({ ...clientePot, notas: e.target.value })}
                  placeholder="Observaciones de la visita, contacto, etc."
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setClientePotOpen(false); setClientePot(null); }}>Cancelar</Button>
            <Button disabled={guardandoCliente} onClick={guardarClientePotencial} className="gap-1">
              {guardandoCliente ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
              Registrar y añadir visita
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
